/**
 * Types for the Camera Cloud Storage Add-on subscription (FE-1666).
 *
 * The PayPal/subscription contract is defined in BE-924
 * (`camera_tiers_expansion` branch on the BE). Camera plans are an
 * independent product from the standard Premium subscription — a user
 * can own either, both, or neither. `u:<uid>.premium` and
 * `u:<uid>.camera` flags are flipped independently.
 *
 * Slot management (assigning / replacing / removing cameras inside the
 * plan's slot pool) is still pending Spas Spasov's backend changes —
 * the FE keeps a local cache of slot assignments meanwhile.
 */

export type CameraCloudStoragePlanTier = 3 | 6 | 10;

/**
 * Plan-type identifiers exposed by BE-924's subscribe / upgrade
 * endpoints. Only the monthly variants are enabled in v1 — yearly
 * camera plans exist in the BE config but are rejected by the API and
 * must be hidden from the UI.
 */
export type CameraCloudStoragePlanType =
	| '3_cameras_monthly'
	| '6_cameras_monthly'
	| '10_cameras_monthly';

export enum CameraCloudStorageSubscriptionStatus {
	Active = 'ACTIVE',
	Cancelled = 'CANCELLED',
	Suspended = 'SUSPENDED',
	Unknown = '',
}

/**
 * A purchasable Camera Cloud Storage plan. `planType` is the BE-924
 * identifier passed into the subscribe / upgrade endpoints.
 */
export interface CameraCloudStoragePlan {
	tier: CameraCloudStoragePlanTier;
	planType: CameraCloudStoragePlanType;
	/** Monthly price in EUR. */
	priceMonthly: number;
	/** Discount badge displayed on the card (e.g. "Save 10%"). */
	savePercent?: number;
}

/**
 * The current (active / paused / cancelled) Camera Cloud Storage
 * subscription for the user. Mirrors the `camera` slot of the
 * `GET /api/subscriptions` response.
 */
export interface CameraCloudStorageActivePlan {
	tier: CameraCloudStoragePlanTier;
	planType?: CameraCloudStoragePlanType;
	status: CameraCloudStorageSubscriptionStatus;
	subscriptionId?: string;
	startedAt?: string;
	nextBillingTime?: string;
}

/**
 * Origin of a Cloud Storage slot. Each Premium subscription includes
 * one slot; additional slots come from a Camera Cloud Storage plan.
 * Extra Premium subscriptions (e.g. a Premium sub-unit) add further
 * Premium slots — `camera_slots.all` is the source of truth for the
 * combined pool.
 */
export type CameraCloudStorageSlotSource = 'premium' | 'subscription';

/**
 * One Cloud Storage slot. The slot is identified by its index (stable
 * within the active plan + premium flag combination). When `cameraId` is
 * null the slot is empty / available.
 */
export interface CameraCloudStorageSlot {
	/** Zero-based index across all of the user's slots. */
	index: number;
	source: CameraCloudStorageSlotSource;
	cameraId: string | null;
}

/**
 * FE-side persisted state for the camera cloud storage feature.
 * Hydrated from `GET /api/subscriptions` (active plan) and augmented
 * locally with the slot assignment map until the slot-assignment BE
 * endpoint exists.
 */
export interface CameraCloudStorageState {
	activePlan: CameraCloudStorageActivePlan | null;
	/** Map of `slotIndex -> cameraId | null`. */
	slotAssignments: Record<number, string | null>;
}

/**
 * `camera_subscription` block inside `user_settings.settings`
 * (`POST /user/get_settings`). The block is **omitted** when the
 * account does not own a camera subscription.
 *
 * Source of truth for active-plan status — replaces the earlier plan
 * to read `/api/subscriptions` (per Spas's clarification).
 */
export interface UserCameraSubscriptionSettings {
	plan_type: CameraCloudStoragePlanType;
	/** Server-side tier identifier, e.g. `6_cameras`. */
	tier: `${CameraCloudStoragePlanTier}_cameras`;
	period: 'monthly';
	status: 'ACTIVE' | 'CANCELLED' | 'SUSPENDED' | string;
	/** Number of slots the subscription itself provides (no Premium addend). */
	slots: number;
	is_active: boolean;
}

/**
 * `camera_slots` block inside `user_settings.settings`. Combines
 * Premium-included slot(s) and the active camera subscription's slot
 * count. `free` decrements as the user assigns cameras to slots.
 *
 * Examples (per Spas's clarification):
 *   - no premium, no plan        →  `{ free: 0, all: 0 }`
 *   - premium, no plan           →  `{ free: 1, all: 1 }`
 *   - premium + 6_cameras_monthly →  `{ free: 7, all: 7 }`
 *   - premium + 6cam, one used    →  `{ free: 6, all: 7 }`
 *   - 2× premium + 3_cameras      →  `{ free: 5, all: 5 }`
 */
export interface UserCameraSlotsSettings {
	free: number;
	all: number;
}

/**
 * Response body of `POST /api/subscribe/...` and
 * `POST /api/subscribe/upgrade/...` (BE-924 §4.1, §4.3). Success
 * (202) returns `{ subscription_id, link }`; the FE must redirect the
 * user to `link` so PayPal can collect approval.
 */
export interface CameraSubscribeResponse {
	subscription_id?: string;
	link?: string;
	error?: string;
	error_code?: number | string;
}

/** Response body of `POST /api/unsubscribe/camera`. */
export interface CameraUnsubscribeResponse {
	camera?: 0 | 1;
	error?: string;
}

/** Single-plan block of `GET /api/subscribe/upgrade-preview/:plantype`. */
export interface CameraUpgradePreviewPlan {
	plan_type: CameraCloudStoragePlanType;
	display_name: string;
	price: number;
	period_days: number;
	remaining_days?: number;
}

/** Response body of `GET /api/subscribe/upgrade-preview/:plantype`. */
export interface CameraUpgradePreviewResponse {
	current_plan: CameraUpgradePreviewPlan;
	target_plan: CameraUpgradePreviewPlan;
	upgrade: {
		is_allowed: boolean;
		compensating_days: number;
		reason?: string;
	};
}

/**
 * Error codes returned by `POST /user/set_camera_list`. The endpoint
 * may also include a `data: { ids }` payload — currently used for
 * `not_a_camera` to point the user at the bad device(s).
 */
export type SetCameraListErrorCode =
	| 'no_devices'
	| 'invalid_devices'
	| 'camera_slots_exceeded'
	| 'not_a_camera';

export interface SetCameraListResponse {
	isok?: boolean;
	error?: SetCameraListErrorCode | string;
	data?: { ids?: string[] };
}
